import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, Search as SearchIcon, Users, Briefcase, Mic, FileText } from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";

const STROKE = 1.5;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [inputVal, setInputVal] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q) { setData(null); return; }
    setLoading(true);
    api.get("/search", { params: { q } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (inputVal.trim()) setParams({ q: inputVal.trim() });
  };

  const total = (data?.total) || 0;

  return (
    <AppShell>
      <div data-testid="search-page" className="space-y-6">
        <header>
          <div className="hp-section-label">Search</div>
          <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">
            Search workspace
          </h1>
          <p className="mt-1 text-[14px] text-ink-500">
            Candidate names, role titles, interview titles, and every word in your transcripts.
          </p>
        </header>

        <form onSubmit={submit} className="relative max-w-xl" data-testid="search-form">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" strokeWidth={STROKE} />
          <input
            autoFocus
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            placeholder="Try a candidate name or a phrase from a transcript…"
            className="hp-input pl-9 pr-24 h-11 text-[15px]"
            data-testid="search-input"
          />
          <button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 hp-btn-primary h-9 px-3" data-testid="search-submit">
            Search
          </button>
        </form>

        {!q && (
          <div className="text-[13px] text-ink-500" data-testid="search-empty-prompt">
            Type a query and press Search.
          </div>
        )}

        {q && loading && (
          <div className="flex items-center gap-2 text-[13px] text-ink-500">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> Searching…
          </div>
        )}

        {q && !loading && data && (
          <div className="space-y-8" data-testid="search-results">
            <div className="text-[13px] text-ink-500" data-testid="search-results-count">
              {total} result{total === 1 ? "" : "s"} for <span className="font-medium text-ink-900">&ldquo;{q}&rdquo;</span>
            </div>
            {data.candidates.length > 0 && (
              <Group icon={Users} title="Candidates">
                {data.candidates.map((c) => (
                  <Link key={c.candidate_id} to={`/candidates/${c.candidate_id}`} className="hp-card block p-4 hover:bg-ink-50 transition" data-testid={`search-candidate-${c.candidate_id}`}>
                    <div className="text-[14px] font-medium text-ink-900">{c.name}</div>
                    {c.email && <div className="mt-0.5 text-[12px] text-ink-500">{c.email}</div>}
                  </Link>
                ))}
              </Group>
            )}
            {data.roles.length > 0 && (
              <Group icon={Briefcase} title="Roles">
                {data.roles.map((r) => (
                  <Link key={r.role_id} to={`/roles/${r.role_id}`} className="hp-card block p-4 hover:bg-ink-50 transition" data-testid={`search-role-${r.role_id}`}>
                    <div className="text-[14px] font-medium text-ink-900">{r.title}</div>
                    {r.department && <div className="mt-0.5 text-[12px] text-ink-500">{r.department}</div>}
                  </Link>
                ))}
              </Group>
            )}
            {data.interviews.length > 0 && (
              <Group icon={Mic} title="Interviews">
                {data.interviews.map((i) => (
                  <Link key={i.interview_id} to={`/interviews/${i.interview_id}`} className="hp-card block p-4 hover:bg-ink-50 transition" data-testid={`search-interview-${i.interview_id}`}>
                    <div className="text-[14px] font-medium text-ink-900">{i.title || "Untitled interview"}</div>
                    <div className="mt-0.5 text-[12px] text-ink-500">{i.type} · Round {i.round_number} · {i.status}</div>
                  </Link>
                ))}
              </Group>
            )}
            {data.transcripts.length > 0 && (
              <Group icon={FileText} title="Transcript matches">
                {data.transcripts.map((t) => (
                  <Link key={t.transcript_id} to={`/interviews/${t.interview_id}`} className="hp-card block p-4 hover:bg-ink-50 transition" data-testid={`search-transcript-${t.transcript_id}`}>
                    <div className="text-[13px] text-ink-700 leading-relaxed">{t.snippet}</div>
                    <div className="mt-2 text-[11px] uppercase tracking-wider text-emerald-800">Open interview →</div>
                  </Link>
                ))}
              </Group>
            )}
            {total === 0 && (
              <div className="hp-card p-8 text-center text-[14px] text-ink-500" data-testid="search-no-results">
                No matches in this workspace.
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Group({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="hp-section-label mb-3 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-emerald-800" strokeWidth={STROKE} /> {title}
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
